import subDays from "date-fns/subDays";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCBadge, RCCButton, RCCGrid, RCCInput, RCCRefModalProps, RCCTooltip } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import Modal from "src/components/Modal";
import Pagination from "src/components/Pagination";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import { IRefIntegrationsLog } from "../interface";

const ListIntegrationLogs: React.ForwardRefRenderFunction<IRefIntegrationsLog> = (_, ref) => {


	const { translate, currentLanguage } = useTranslation();

	const paginationRef = useRef<IForwardPagination>(null);
	const refModal = useRef<RCCRefModalProps>(null);

	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string , endDate: Date | null, initDate: Date | null}>({ filter: "", endDate: new Date(), initDate: subDays(new Date(),30) });
	const { handleSubmit : handleSubmitFilters, setValue: setValueFilters, control: controlFilters, register : registerFilters, reset: resetFilters, watch: watchFilters } = useForm();

	const [ selectedRow, setSelectedRow ] = useState<{ id: number, name: string } | null>(null);

	const submitFilters = (data: any) => {
		setFilters({ 
			filter: data.filter, 
			endDate: data.endDate,
			initDate: data.initDate
		});
		paginationRef.current?.reload();
	};


	const actionButtonOpen = (id: number, name: string) => {
		resetFilters();
		setShowFilters(false);
		setSelectedRow({ id, name });
		refModal.current?.openModal();
	};

	const colSituacao = (row: any) => (
		<RCCBadge color={row.iql004Situacao === 0 ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.iql004Situacao === 0 ? translate("Sucesso") : translate("Falha")}
		</RCCBadge>
	);

	useImperativeHandle(ref, () => ({
		actionButtonOpen
	}));


	return (
		<>

			<Modal
				ref={refModal}
				header={{
					title:  translate("Logs de Integração"),
					subtitle: `${translate("Log de Integração")}: ${selectedRow ? selectedRow.name : ""}` ,
					icon: {
						name: "stack"
					}
				}}
				withoutForm
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
					> 
						<RCCTooltip
							text={translate("Filtros")}
					  	color={showFilters || filters.initDate || filters.endDate || filters.filter ? "blue" : "grey"}
							position="leftEnd"
						>
							<RCCButton
								icon={{name: "filter", color: "#FFF", size: 16}}
								color={showFilters || filters.initDate || filters.endDate || filters.filter  ? "blue" : "grey"}
								layout="isRounded"
								size="md"
						  	onClick={() => setShowFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>
					</RCCGrid>

    	    {/* Filters */}
					{showFilters && (
						<FiltersContainer
							onSubmitFilters={handleSubmitFilters(submitFilters)}
							resetFilters={() => resetFilters(
								{ filter: "", endDate: null, initDate: null})}
						>
							<RCCGrid
								container
								item
							>
								<RCCGrid
									container
									item
									direction="row"
									spacing={1}
									xs={10}
								>
									<RCCGrid
										container 
										item
										xs={12}
										sm={6}
										md={4}
										lg={4}
									>
										<DateTimePickerRange 
											type="datePicker"
											label={translate("Período")}
											labelSeparator={translate("até")}
											nameInit="initDate"
											nameEnd="endDate"
											setValue={setValueFilters}
											control={controlFilters}
											defaultValueEnd={filters.endDate}
											defaultValueInit={filters.initDate}	
										/> 
									</RCCGrid>
									<RCCGrid
										container 
										item
										xs={12}
										sm={8}
										md={4}
										lg={4}
									>
										<RCCInput
											name="filter" label={translate("Pesquisar")}
											register={registerFilters}
											defaultValue={watchFilters("filter")}
										/>
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						</FiltersContainer>
					)}

					<Pagination 
						ref={paginationRef}
						dataOrRoute="Integrations/ListIntegrationLogs" 
						filtersData={{
							...filters,
							initDate: filters.initDate ? filters.initDate.toLocaleDateString() : "",
							endDate: filters.endDate ? filters.endDate.toLocaleDateString() : "",
							iqb001Id: selectedRow ? selectedRow.id : 0
						}} 				
						columns={[
							{
								idColumn: "iql004Data",
								nameColumn: translate("Última Integração"),
								xs: 12,
								sm: 2,
								justify: "left",
								type: "string",
								cellRenderer: (cell: any) => formatDate(cell.iql004Data, currentLanguage, true),
								truncate: true
							},
							{
								idColumn: "Iqb004Codigo",
								nameColumn: translate("Período"),
								xs: 12,
								sm: 2,
								justify: "left",
								truncate: true,
								cellRenderer: (cell: any) => `${formatDate(cell.iqb004DataIni, currentLanguage)} - ${formatDate(cell.iqb004DataFim, currentLanguage)}`
							},
							{
								idColumn: "efa015Estacao",
								nameColumn: translate("Situação"),
								xs: 12,
								sm: 2,
								justify: "left",
								type: "string",
								truncate: true,
								cellRenderer: colSituacao
							},
							{
								idColumn: "iql004Mensagem",
								nameColumn: translate("Mensagem"),
								xs: 12,
								sm: 6,
								justify: "left",
								type: "string",
								truncate: true
							}
						]}			
					/>

				</RCCGrid>
			</Modal>


		
		</>
	);
};

export default forwardRef(ListIntegrationLogs);
